import { Capacitor } from '@capacitor/core'
import { t } from '@lingui/macro'
import { useEffect, useRef } from 'react'
import styled, { useTheme } from 'styled-components'

import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout/flex/Flex'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { mixpanel } from '@/mixpanel'
import { InfiniteScrollList } from '@/modules/shared/components/infinite-scroll-list/InfiniteScrollList'
import { Loader } from '@/modules/shared/components/loader/Loader'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { TaskCenterTab } from '../../pages/TaskRoot.page'
import { TaskItem } from '../task-item/TaskItem'

import type { Task } from '../../types/task.types'

type TaskListProps = {
  /**
   * The id of the task that is currently selected
   */
  selectedTaskId?: string

  /**
   * The list of tasks to display
   */
  tasks: Task[]

  /**
   * Whether there are more tasks to load
   */
  hasMore: boolean

  /**
   * Whether the list is loading
   */
  isLoading: boolean

  /**
   * The active tab (pending or resolved)
   */
  activeTab: TaskCenterTab

  /**
   * The callback to call when the list is scrolled to the bottom
   */
  onEndReached: () => void

  /**
   * The callback to call when a task is selected
   */
  onTaskSelected: (taskId: string) => void
}

export const TaskList = ({
  selectedTaskId,
  tasks,
  hasMore,
  isLoading,
  activeTab,
  onTaskSelected,
  onEndReached,
}: TaskListProps) => {
  // Refs
  // ------------------------------------------------------------------------- /
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()

  // Methods
  // ------------------------------------------------------------------------- /
  const keyExtractor = (task: Task) => task.id

  // Render
  // ------------------------------------------------------------------------- /
  const renderTaskItem = (task: Task) => {
    return (
      <StListItem
        key={task.id}
        id={`task_${task.id}`}
        onClick={() => onTaskSelected(task.id)}
        data-active={selectedTaskId && task?.id ? task?.id === selectedTaskId : undefined}
      >
        <TaskItem task={task} />
      </StListItem>
    )
  }

  return (
    <StyledScrollableListContainer ref={scrollContainerRef}>
      <InfiniteScrollList<Task>
        items={tasks}
        renderItem={renderTaskItem}
        keyExtractor={keyExtractor}
        onEndReached={onEndReached}
        hasMore={hasMore}
        showDividers={Capacitor.isNativePlatform()}
        scrollableRootRef={scrollContainerRef}
        ListFooterComponent={
          isLoading && tasks.length > 0 ? (
            <div style={{ height: '50px' }}>
              <Loader small />
            </div>
          ) : null
        }
        ListEmptyComponent={
          isLoading ? (
            <Loader />
          ) : (
            <EmptyListIndicator>
              {activeTab === TaskCenterTab.Pending ? (
                <>
                  <TrackView event='task_empty_state_shown_open' />
                  <EmptyListImage
                    src='/img/empty-pending-task-center.png'
                    alt={t({
                      id: 'task.empty-open-tasks',
                      message: 'Je hebt geen openstaande taken',
                    })}
                  />
                </>
              ) : (
                <>
                  <TrackView event='task_empty_state_shown_closed' />
                  <EmptyListImage
                    src='/img/empty-resolved-task-center.png'
                    alt={t({
                      id: 'task.empty-resolved-tasks',
                      message: 'Je hebt nog geen afgewerkte taken',
                    })}
                  />
                </>
              )}
              <FlexColumn gap={theme.UI.SpacingPx.Space2} alignItems='center'>
                <H3 noMargin>
                  {activeTab === TaskCenterTab.Pending
                    ? t({
                        id: 'task.empty-open-tasks',
                        message: 'Je hebt geen openstaande taken',
                      })
                    : t({
                        id: 'task.empty-resolved-tasks',
                        message: 'Je hebt nog geen afgewerkte taken',
                      })}
                </H3>
                <P
                  type='BodySmallRegular'
                  style={{ padding: `0 ${theme.UI.SpacingPx.Space3}` }}
                  color={theme.Colors.Neutrals.Darker}
                >
                  {activeTab === TaskCenterTab.Pending
                    ? t({
                        id: 'task.empty-open-tasks-description',
                        message: 'Je ontvangt hier een overzicht van je taken zodra er iets voor je klaarstaat',
                      })
                    : t({
                        id: 'task.empty-resolved-tasks-description',
                        message: 'Hier volgt later een overzicht van al je afgewerkte taken.',
                      })}
                </P>
              </FlexColumn>
            </EmptyListIndicator>
          )
        }
      />
    </StyledScrollableListContainer>
  )
}

const StyledScrollableListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  height: 100%;
  padding-top: ${({ theme }) => theme.UI.SpacingPx.Space4};

  @media ${breakpoints.fromL} {
    padding-left: ${({ theme }) => theme.UI.SpacingPx.Space12};
  }
`

const EmptyListIndicator = styled(FlexColumn)`
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space8};

  text-align: center;
  height: 100%;
`

const EmptyListImage = styled.img`
  width: 60vw;
  height: auto;
  min-width: 200px;
  object-fit: contain;

  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
`

const StListItem = styled(FlexRow)`
  width: 100%;
  cursor: pointer;
  position: relative;
  background-color: ${({ theme }) => theme.Colors.Background.White};
  transition: background 0.2s ease-in-out;
  margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space1};

  padding: ${({ theme }) => theme.UI.SpacingPx.Space6}
    ${({ theme }) => theme.UI.SpacingPx.Space6};

  @media ${breakpoints.fromM} {
    padding: ${({ theme }) => theme.UI.SpacingPx.Space6}
      ${({ theme }) => theme.UI.SpacingPx.Space8};
  }

  @media ${breakpoints.fromL} {
    border-radius: 12px 0 0 12px;
    background: ${({ theme }) => theme.Components.Card.Fill};
    transition: background 0.2s ease-in-out;
  }

  &[data-active='true'] {
    background: ${({ theme }) => theme.Colors.Neutrals.Lighter};
  }
  &:hover {
    background: ${({ theme }) => theme.Colors.Neutrals.Lighter};
  }
`

function TrackView({ event }: { event: string }) {
  const isLoggedInForMandatee = useLoggedInForMandatee()

  useEffect(() => {
    mixpanel.track(event, { origin, is_mandate: isLoggedInForMandatee })
  }, [event])

  return null
}
